/**
 * AgentPodPanel — grid of agent status cards with start/stop controls.
 * P&L and win rate come from the DB-backed leaderboard, never from the
 * in-memory agent status (which hardcodes 0.0 for both).
 */

import {
  useAgentLeaderboard,
  useAgentsOverview,
  useStartAgent,
  useStopAgent,
} from "@/sdk/hooks/use-agents";
import { useTerminalStore } from "@/stores/terminal-store";
import type { AgentStatus, LeaderboardEntry } from "@/sdk/types/agents";

const STATE_COLORS: Record<string, string> = {
  running: "bg-emerald-500",
  idle: "bg-zinc-500",
  stopped: "bg-zinc-600",
  error: "bg-red-500",
};

function AgentCard({
  agent,
  dbStats,
}: {
  agent: AgentStatus;
  dbStats?: LeaderboardEntry;
}) {
  const start = useStartAgent();
  const stop = useStopAgent();
  const { selectAgent, selectedAgentId } = useTerminalStore();
  const isSelected = selectedAgentId === agent.agent_id;
  const isRunning = agent.state === "running" || agent.running === true;

  const actionError = stop.isError ? stop.error : start.isError ? start.error : null;
  const actionLabel = stop.isError ? "Stop failed" : "Start failed";

  const pnl = dbStats?.total_pnl != null ? Number(dbStats.total_pnl) : null;
  const winRate = dbStats?.win_rate != null ? Number(dbStats.win_rate) : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => selectAgent(agent.agent_id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") selectAgent(agent.agent_id); }}
      className={`w-full text-left cursor-pointer rounded-lg border p-3 transition-all ${
        isSelected
          ? "border-indigo-500 bg-zinc-900/80"
          : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
      }`}
    >
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${STATE_COLORS[agent.state ?? (agent.running ? "running" : "stopped")] ?? "bg-zinc-500"}`}
          />
          <span className="text-sm font-medium text-white">{agent.name ?? agent.agent_name ?? agent.agent_id}</span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isRunning) {
              stop.mutate(agent.agent_id);
            } else {
              start.mutate(agent.agent_id);
            }
          }}
          disabled={start.isPending || stop.isPending}
          className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${
            isRunning
              ? "bg-red-950 text-red-400 hover:bg-red-900"
              : "bg-emerald-950 text-emerald-400 hover:bg-emerald-900"
          }`}
        >
          {isRunning ? "Stop" : "Start"}
        </button>
      </div>

      {actionError != null && (
        <p role="alert" className="mb-2 text-[10px] font-semibold text-red-400">
          {actionLabel}
          {actionError instanceof Error && actionError.message ? `: ${actionError.message}` : ""}
        </p>
      )}

      {/* Stats grid — P&L / win rate are DB facts; absent read renders "—",
          never a fake $0.00 */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="text-zinc-500">P&L</div>
        <div
          className={`text-right font-mono ${
            pnl == null ? "text-zinc-500" : pnl >= 0 ? "text-emerald-400" : "text-red-400"
          }`}
        >
          {pnl != null ? `$${pnl.toFixed(2)}` : "—"}
        </div>
        <div className="text-zinc-500">Positions</div>
        <div className="text-right font-mono text-zinc-300">
          {agent.open_positions}
        </div>
        <div className="text-zinc-500">Win Rate</div>
        <div className="text-right font-mono text-zinc-300">
          {winRate != null ? `${(winRate * 100).toFixed(1)}%` : "—"}
        </div>
        <div className="text-zinc-500">Trades</div>
        <div className="text-right font-mono text-zinc-300">
          {agent.total_trades}
        </div>
      </div>

      {/* Strategy + Paper badge */}
      <div className="mt-2 flex items-center gap-2">
        <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-400">
          {agent.strategy}
        </span>
        {agent.paper_trading && (
          <span className="rounded bg-amber-950 px-1.5 py-0.5 text-[10px] text-amber-400">
            Paper
          </span>
        )}
      </div>
    </div>
  );
}

export function AgentPodPanel({ agentId }: { agentId?: string }) {
  const { data: overview, isLoading, isError } = useAgentsOverview();
  const { data: leaderboard } = useAgentLeaderboard();
  const agents = overview?.agents ? Object.values(overview.agents) : [];
  const visibleAgents = agentId
    ? agents.filter((agent) => agent.agent_id === agentId)
    : agents;

  const statsByAgent = new Map(
    (leaderboard?.leaderboard ?? []).map((entry) => [entry.agent_id, entry]),
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-zinc-500 text-sm">
        Loading agents...
      </div>
    );
  }

  if (visibleAgents.length === 0) {
    // An errored overview read means "unavailable", not an empty registry.
    if (isError) {
      return (
        <div className="flex h-full items-center justify-center text-xs text-amber-400">
          Agent data unavailable — backend unreachable
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center text-zinc-500 text-sm">
        {agentId ? "Selected agent not found" : "No agents registered"}
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-zinc-950 p-2">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
        {visibleAgents.map((agent) => (
          <AgentCard
            key={agent.agent_id}
            agent={agent}
            dbStats={statsByAgent.get(agent.agent_id)}
          />
        ))}
      </div>
    </div>
  );
}
